import React from 'react';
import {formatDate} from '../../utils';
import {DetailField} from './DetailField';
import {EventDetailDto} from '../../generated/models/EventDetailDto';
import {TagContainer} from './TagContainer';
import {useCategoriesWithTags} from '../../common/hooks/useCategories';
import {mapTagCodesTagNames} from '../../utils/mappers';
import {RecurrenceInfo} from './RecurrenceInfo';

export const EventDetailsCard: React.FC<{event: EventDetailDto}> = ({event}) => {
  const {data: categoriesWithTags} = useCategoriesWithTags();

  const containerStyle: React.CSSProperties = {
    backgroundColor: '#2a2a2a', // Matching your dashboard input colors
    borderRadius: '16px',
    padding: '32px',
    width: '100%',
    maxWidth: '600px',
    margin: '40px auto',
    color: '#fff',
    boxShadow: '0 20px 40px rgba(0,0,0,0.4)',
    border: '1px solid #333',
  };

  return (
    <div style={containerStyle}>
      {/* Header Section */}
      <div
        style={{
          borderBottom: '1px solid #444',
          paddingBottom: '20px',
          marginBottom: '30px',
        }}>
        <h1 style={{margin: 0, fontSize: '1.8rem', color: '#daff00'}}>
          {event.name}
        </h1>
      </div>

      {/* Info Grid */}
      <div style={{display: 'flex', flexWrap: 'wrap', gap: '24px'}}>
        <DetailField
          label="Description"
          value={event.description || ''}
          fullWidth
        />
        <DetailField label="Starts" value={formatDate(event.occurrences[0]?.dateTimeStart)} />
        <DetailField label="Ends" value={formatDate(event.occurrences[0]?.dateTimeStop)} />

        {event.occurrences?.length > 1 && (
          <div style={{width: '100%'}}>
            <RecurrenceInfo occurrences={event.occurrences} expandable darkBackground />
          </div>
        )}

        <div
          style={{
            width: '100%',
            height: '1px',
            backgroundColor: '#444',
            margin: '10px 0',
          }}
        />

        {event.address.placeName && (
          <DetailField label="Place Name" value={event.address.placeName} />
        )}
        <DetailField label="Country" value={event.address.country ?? ''} />
        <DetailField label="City" value={event.address.cityName ?? ''} />
        <DetailField
          label="Address"
          value={`${event.address.streetName ?? ''} ${event.address.streetNumber ?? ''}`}
        />
      </div>

      {event.mapUri && (
        <div style={{marginTop: '40px', marginBottom: '40px'}}>
          <label
            style={{
              color: '#daff00',
              fontSize: '0.8rem',
              fontWeight: 700,
              textTransform: 'uppercase',
              display: 'block',
              marginBottom: '15px',
            }}>
            Map
          </label>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
              gap: '16px',
            }}>
            <img
              src={event.mapUri}
              alt={`Map`}
              style={{
                width: '100%',
                aspectRatio: '16/9',
                objectFit: 'cover',
                borderRadius: '12px',
                border: '2px solid #333',
              }}
            />
          </div>
        </div>
      )}

      {/* Photo Gallery Section */}
      {event.mapImageCdnPaths.length > 0 && (
        <div style={{marginTop: '40px'}}>
          <label
            style={{
              color: '#daff00',
              fontSize: '0.8rem',
              fontWeight: 700,
              textTransform: 'uppercase',
              display: 'block',
              marginBottom: '15px',
            }}>
            Event Gallery
          </label>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
              gap: '16px',
            }}>
            {event.mapImageCdnPaths.map((uri, index) => (
              <img
                key={index}
                src={uri}
                alt={`Event detail ${index}`}
                style={{
                  width: '100%',
                  aspectRatio: '16/9',
                  objectFit: 'cover',
                  borderRadius: '12px',
                  border: '2px solid #333',
                }}
              />
            ))}
          </div>
        </div>
      )}

      <hr style={{margin: 16}} />

      {categoriesWithTags?.length && (
        <TagContainer
          tags={mapTagCodesTagNames(event?.tagCodes || [], categoriesWithTags)}
        />
      )}
    </div>
  );
};
